import { useEffect, useState } from 'react'
import { api, auth } from '../lib/api.js'
import DateRangePicker from '../components/DateRangePicker.jsx'
import { SUCCESS_OUTCOMES } from '../lib/outcomes.js'
import { onChange } from '../lib/changes.js'
import { formatBeijingTime, beijingDateString } from '../lib/time.js'

// 统计接口返回的列名 → 结果类型，顺序跟下拉保持一致
const BREAKDOWN = [
  ['s360', 'success_360'], ['s180', 'success_180'], ['s158', 'success_158'],
  ['scredit', 'success_credit'],
].map(([col, key]) => ({ col, label: SUCCESS_OUTCOMES.find(o => o.key === key).label }))

const RANGES = [
  { days: 1, label: '今天' },
  { days: 7, label: '近 7 天' },
  { days: 30, label: '近 30 天' },
]

// 百分比格式：模块级常量，必须在用到它的组件之前声明（no-use-before-define 已开成 error）
const pct = (a, b) => b ? Math.round(a / b * 100) + '%' : '—'

export default function Stats() {
  const isAdmin = auth.isAdmin
  const [days, setDays] = useState(1)   // 默认看今天（2026-09-30 Gavin 定）
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [agentId, setAgentId] = useState('')
  const [data, setData] = useState(null)
  const [agents, setAgents] = useState([])
  const [paid, setPaid] = useState(null)   // 车网通同期收款（成交口径）
  const [err, setErr] = useState('')

  useEffect(() => { if (isAdmin) api.listAgents().then(rows => setAgents(rows.filter(a => a.role === 'agent'))).catch(() => {}) }, [isAdmin])
  useEffect(() => {
    setErr('')
    const p = { agentId: agentId || undefined }
    if (from && to) { p.from = from; p.to = to } else { p.days = days }
    let alive = true, busy = false
    const load = async () => {
      if (busy) return
      busy = true
      try { const result = await api.getReport(p); if (alive) { setData(result); setErr('') } }
      catch (e) { if (alive) setErr(e.message) }
      finally { busy = false }
    }
    load()
    const off = onChange(load)
    const timer = setInterval(load, 15000)
    return () => { alive = false; off(); clearInterval(timer) }
  }, [days, from, to, agentId])

  // 车网通同期收款：加微信成交的单没有扫码进线，我们自己的记录数不到，所以成交口径以车网通为准。
  // 区间拿不到就安静地不显示（车网通那个统计接口本身不稳）。
  useEffect(() => {
    if (!isAdmin) return
    const p = from && to ? { from, to } : { from: undefined, to: undefined }
    if (!from && !to) {
      // 必须按**北京时间**算日期：以前用 Date.now()+8h 再 toISOString()，浏览器不在东八区时（如洛杉矶）
      // 当天下午算出来的还是前一天（2026-10-12 审查指出）
      const endTs = Date.now()
      p.to = beijingDateString(endTs)
      p.from = beijingDateString(endTs - (days - 1) * 86400000)
    }
    let alive = true
    api.getCwtPaid(p).then(v => alive && setPaid(v)).catch(() => alive && setPaid(null))
    return () => { alive = false }
  }, [from, to, days, isAdmin])

  // 车网通付款量只按「区间」取：单日区间时它就是当天数，逐行显示；
  // 多日区间没有逐日数据（每天都要单独问车网通，30 天 × 几组 = 上百次请求，他们接口也不稳），
  // 这时列里留 —，看上方「车网通收款（成交口径）」卡片的总数。
  const useRange = !!(from && to)
  const pickPreset = (d) => { setFrom(''); setTo(''); setDays(d) }
  const s = data?.summary
  const showAgent = isAdmin && !agentId

  return (
    <div style={{ padding: '28px 40px', maxWidth: 1320 }}>
      <div style={{ fontSize: 20, fontWeight: 600 }}>数据统计</div>
      <div className="muted small" style={{ marginTop: 4, marginBottom: 20 }}>
        {isAdmin ? '按天统计各运营的进线、成交与成功率' : '仅显示您本人的数据'}
      </div>

      <div className="row" style={{ gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {RANGES.map(r => (
          <button key={r.days} className="btn btn-sm" onClick={() => pickPreset(r.days)}
            style={(!useRange && r.days === days) ? { background: 'var(--brand)', borderColor: 'var(--brand)', color: '#fff' } : {}}>
            {r.label}
          </button>
        ))}
        <DateRangePicker from={from} to={to}
          onChange={(a, b) => { setFrom(a); setTo(b); if (!a) setDays(7) }} />
        {isAdmin && (
          <select className="input" style={{ width: 180, marginLeft: 8 }}
            value={agentId} onChange={e => setAgentId(e.target.value)}>
            <option value="">全部运营</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
          </select>
        )}
      </div>

      {err && <div className="err">{err}</div>}
      {data?.orderSync && <div className="muted small" style={{ marginBottom: 12 }}>
        {data.from} 至 {data.to} · 北京时间
        {data.syncedAt && ` · 订单更新 ${formatBeijingTime(data.syncedAt, { hour12: false })}`}
      </div>}
      {data?.coverage && !data.coverage.complete && <div role="status" className="err" style={{ marginBottom: 12 }}>
        历史待核对 {data.coverage.totalPendingConversations} 条会话，本人或当前筛选范围涉及 {data.coverage.pendingConversations} 条，
        已知日期落在本期 {data.coverage.periodAffectedConversations} 条。以下为已核实统计，历史补齐后可能调整。
      </div>}

      {/* 第一行：进线（按实际进线那天）；第二行：订单（成交 / 失败按标记那天）+ 方案构成 */}
      <div className="stats-grid">
        <Card label="总进线" value={s?.total ?? '—'} />
        <Card label="新客" value={s?.fresh ?? '—'} color="var(--brand)" />
        <Card label={`老客（售后 ${s?.aftersale ?? '—'}）`} value={s?.ret ?? '—'} color="#8b5cf6" />
        <Card label="有效进线" value={s?.effective ?? '—'} color="var(--brand)" />
        <Card label="跟进中" value={s?.following ?? '—'} color="#d97706" />
        <Card label="未标记" value={s?.pending ?? '—'} color="var(--muted)" />
        <Card label="失败" value={s?.failed ?? '—'} color="var(--danger)" />
        <Card label="成功率" value={s ? pct(s.success, s.effective) : '—'} color="var(--brand)" />

        <Card label="车网通收款（成交口径）" value={paid?.total ?? '—'} color="var(--ok)" />
        <Card label="成交（按标记）" value={s?.success ?? '—'} color="#0ea5e9" />
        <Card label="AI 成交 / 人工成交" value={s ? `${s.ai_win} / ${s.human_win}` : '—'} color="#6366f1" />
        <div className="card stats-span6" style={{
          padding: '16px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-around', flexWrap: 'wrap', gap: 16,
        }}>
          {BREAKDOWN.map(b => (
            <div key={b.col} style={{ textAlign: 'center' }}>
              <div className="muted small">{b.label}</div>
              <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>{s?.[b.col] ?? '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 车网通同期收款：按运营，成交口径以它为准（加微信成交的单没有进线，我们的记录数不到） */}
      {paid && Object.keys(paid.byAgent || {}).length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <strong style={{ display: 'block', marginBottom: 12 }}>车网通收款（按运营 · {paid.from} ~ {paid.to}）</strong>
          <table>
            <thead><tr><th>运营</th><th style={{ textAlign: 'right' }}>收款数量（成交）</th>
              <th style={{ textAlign: 'right' }}>接单量</th><th style={{ textAlign: 'right' }}>收款率</th></tr></thead>
            <tbody>
              {Object.entries(paid.byAgent).sort((a, b) => b[1].paid - a[1].paid).map(([id, v]) => (
                <tr key={id}>
                  <td>{v.name}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{v.paid}</td>
                  <td style={{ textAlign: 'right' }}>{v.orders}</td>
                  <td style={{ textAlign: 'right' }}>{v.paidRate || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 进线明细：新客、老客分开看转化（2026-10-05 改版时被误删，按 Gavin 要求恢复） */}
      <EntryStats data={data} showAgent={showAgent} isAdmin={isAdmin} paid={paid} />

      <div className="card" style={{ padding: 20 }}>
        <strong style={{ display: 'block', marginBottom: 14 }}>每日明细</strong>
        {!data?.rows.length ? (
          <div className="empty">该时间段内暂无记录</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>日期</th>
                {showAgent && <th>运营</th>}
                <th style={{ textAlign: 'right' }}>进线</th>
                <th style={{ textAlign: 'right' }}>新客</th>
                <th style={{ textAlign: 'right' }}>老客</th>
                <th style={{ textAlign: 'right' }}>售后</th>
                <th style={{ textAlign: 'right' }}>有效进线</th>
                <th style={{ textAlign: 'right' }}>跟进中</th>
                <th style={{ textAlign: 'right' }}>未标记</th>
                <th style={{ textAlign: 'right' }}>成交（按标记）</th>
                <th style={{ textAlign: 'right' }}>失败</th>
                <th style={{ textAlign: 'right' }}>成功率</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.day + (r.agent_id || '') + i}>
                  <td>{i === 0 || data.rows[i - 1].day !== r.day ? r.day : ''}</td>
                  {showAgent && <td>{r.agent_name}</td>}
                  <td style={{ textAlign: 'right' }}>{r.total}</td>
                  <td style={{ textAlign: 'right' }}>{r.fresh}</td>
                  <td style={{ textAlign: 'right' }}>{r.ret}</td>
                  <td style={{ textAlign: 'right', color: '#8b5cf6' }}>{r.aftersale}</td>
                  <td style={{ textAlign: 'right' }}>{r.effective}</td>
                  <td style={{ textAlign: 'right', color: '#d97706' }}>{r.following}</td>
                  <td style={{ textAlign: 'right' }} className="muted">{r.pending}</td>
                  <td style={{ textAlign: 'right', color: 'var(--ok)' }}>{r.success}</td>
                  <td style={{ textAlign: 'right', color: 'var(--danger)' }}>{r.failed}</td>
                  <td style={{ textAlign: 'right' }}>{pct(r.success, r.effective)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {isAdmin && !data?.orderSync && (
          <div className="small muted" style={{ marginTop: 12, padding: 10, background: '#fffbeb', borderRadius: 8, color: '#b45309' }}>
            客户按<strong>车牌 / 手机号</strong>认（没留的按微信号），按<strong>归属人</strong>统计（归属取客户最新一通会话）。
            <strong>进线</strong>按实际扫码进来那天；<strong>老客</strong>＝之前来过、或结果日期选在进线日之前的（旧企微转来的），其中之前已成交的是<strong>售后</strong>；
            <strong>有效进线</strong>＝进线里今天之前没成交、没失败的；跟进中 / 未标记是这些进线客户现在的状态。
            <strong>成交 / 失败</strong>按标记时选的日期算，一个客户一天只算一单。成功率＝成交 ÷ 有效进线。
          </div>
        )}
      </div>
    </div>
  )
}

function Num({ label, value, color, big }) {
  return (
    <div style={{ minWidth: big ? 84 : 56 }}>
      <div className="muted small">{label}</div>
      <div style={{ fontSize: big ? 26 : 21, fontWeight: 600, marginTop: 2, color: color || 'var(--text)' }}>
        {value ?? '—'}
      </div>
    </div>
  )
}

// 当天进线的客户按新客 / 老客拆开看：成交、失败是这批客户当天标的；跟进中、未标记是他们现在的状态
function EntryStats({ data, showAgent, isAdmin, paid }) {
  const s = data?.summary
  // 付款量（车网通）：**按当前所选区间**取（车路网后台本身就是按区间统计的，我们传同样的 from/to）。
  // 区间 = 一天时它就是当天数；区间多天时它是该运营在这个区间的合计，所以只在本人第一行显示，避免一天一行重复刷同一串数字。
  const paidByAgent = paid?.byAgent || {}
  const firstRowOfAgent = new Map()
  for (const [i, r] of (data?.rows || []).entries()) {
    const k = r.agent_id || ''
    if (!firstRowOfAgent.has(k)) firstRowOfAgent.set(k, i)
  }
  const paidFor = (row, i) => {
    if (!row.agent_id || !paid) return '—'
    if (firstRowOfAgent.get(row.agent_id) !== i) return ''
    const v = paidByAgent[row.agent_id]
    return v ? v.paid : '—'
  }
  return (
    <div className="card" style={{ padding: '16px 20px', marginBottom: 20, borderLeft: '3px solid var(--brand)' }}>
      <div className="spread" style={{ marginBottom: 14 }}>
        <strong>进线明细</strong>
        <span className="muted small">新客 / 老客各自的转化</span>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 300px', background: 'var(--bg)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>新客情况 · {s?.fresh ?? '—'}</div>
          <div className="muted small" style={{ marginBottom: 10 }}>第一次进线，真正要去转化的量</div>
          <div className="row" style={{ gap: 20, flexWrap: 'wrap' }}>
            <Num label="成交" value={s?.fresh_success} color="var(--ok)" />
            <Num label="跟进中" value={s?.fresh_following} color="#d97706" />
            <Num label="失败" value={s?.fresh_failed} color="var(--danger)" />
            <Num label="未标记" value={s?.fresh_pending} color="var(--muted)" />
            <Num label="成交率" value={s ? pct(s.fresh_success, s.fresh) : null} color="var(--brand)" />
          </div>
        </div>

        <div style={{ flex: '1 1 300px', background: 'var(--bg)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>老客情况 · {s?.ret ?? '—'}</div>
          <div className="muted small" style={{ marginBottom: 10 }}>之前来过、或旧企微转来的客户又进线了</div>
          <div className="row" style={{ gap: 20, flexWrap: 'wrap' }}>
            <Num label="成交" value={s?.ret_success} color="var(--ok)" />
            <Num label="售后" value={s?.aftersale} color="#8b5cf6" />
            <Num label="跟进中" value={s?.ret_following} color="#d97706" />
            <Num label="失败" value={s?.ret_failed} color="var(--danger)" />
            <Num label="未标记" value={s?.ret_pending} color="var(--muted)" />
            <Num label="成交率" value={s ? pct(s.ret_success, s.ret) : null} color="var(--brand)" />
          </div>
        </div>
      </div>

      {!!data?.rows.length && (
        <table style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>日期</th>
              {showAgent && <th>归属运营</th>}
              <th style={{ textAlign: 'right' }}>进线</th>
              <th style={{ textAlign: 'right' }}>新客</th>
              <th style={{ textAlign: 'right' }}>新客成交</th>
              <th style={{ textAlign: 'right' }} title="车路网后台按所选区间统计的收款数量（加微信成交的单没有扫码进线，我们自己的记录数不到）；多日区间显示该运营的区间合计，只写第一行">付款量（车网通·所选区间）</th>
              <th style={{ textAlign: 'right' }}>新客成交率</th>
              <th style={{ textAlign: 'right' }}>老客</th>
              <th style={{ textAlign: 'right' }}>售后</th>
              <th style={{ textAlign: 'right' }}>老客成交</th>
              <th style={{ textAlign: 'right' }}>老客成交率</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i}>
                <td>{i === 0 || data.rows[i - 1].day !== r.day ? r.day : ''}</td>
                {showAgent && <td>{r.agent_name}</td>}
                <td style={{ textAlign: 'right' }}>{r.total}</td>
                <td style={{ textAlign: 'right' }}>{r.fresh}</td>
                <td style={{ textAlign: 'right', color: 'var(--ok)' }}>{r.fresh_success}</td>
                <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--brand)' }}>{paidFor(r, i)}</td>
                <td style={{ textAlign: 'right' }}>{pct(r.fresh_success, r.fresh)}</td>
                <td style={{ textAlign: 'right' }}>{r.ret}</td>
                <td style={{ textAlign: 'right', color: '#8b5cf6' }}>{r.aftersale}</td>
                <td style={{ textAlign: 'right', color: 'var(--ok)' }}>{r.ret_success}</td>
                <td style={{ textAlign: 'right' }}>{pct(r.ret_success, r.ret)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isAdmin && !data?.orderSync && (
        <div className="small muted" style={{ marginTop: 12, padding: 10, background: '#fffbeb', borderRadius: 8, color: '#b45309' }}>
          新客 / 老客按<strong>实际进线那天</strong>区分：之前来过、或结果日期选在进线日之前（旧企微转来的）就是老客，其中之前成交过的是售后。
          这里的成交 / 失败只算<strong>当天进线的客户当天标的</strong>；跟进中 / 未标记是他们现在的状态。
        </div>
      )}
    </div>
  )
}

function Card({ label, value, color }) {
  return (
    <div className="card" style={{ padding: '16px 20px', minWidth: 0 }}>
      <div className="muted small">{label}</div>
      <div style={{ fontSize: 26, fontWeight: 600, marginTop: 6, color: color || 'var(--text)' }}>{value}</div>
    </div>
  )
}
